// Full-fidelity news view. The Dashboard card only renders LLM summaries;
// this page dumps every field we receive from /api/admin/dashboard/news so
// an operator can cross-reference the summariser's conclusions against the
// raw feed (ticker / service / severity / category / published_at / body).
//
// Unlike the dashboard: no summarisation, no staleTime pinning — refetch
// on mount is what you want when you arrive specifically to inspect the
// feed.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  type DashboardNews,
  type NewsItem,
  type NewsQueueResponse,
  type NewsService,
  type NewsSentiment,
  type NewsSummaryItem,
} from '@/api/client'
import { Badge, Button, Card, Input } from '@/admin-design/_common'
import { formatRelativeKST, formatTime } from '@/lib/utils'
import { krxName } from '@/lib/krx-names'

// PAGE_SIZE is the stock-side (holdings + market_wide) page size the
// server paginates on. Crypto bucket is separately fetched and not
// paged — it's low-volume enough today to show in one slice.
const PAGE_SIZE = 50

export default function News() {
  const qc = useQueryClient()
  // Pagination over the stock-side feed. hours=0 drops the lower
  // bound so the operator can page back to retention boundary, not
  // just 24h. Resetting to page 0 on service filter changes would be
  // cleaner but adds complexity — for now the filter tabs just
  // client-side filter whatever the current page returned.
  const [page, setPage] = useState(0)
  const q = useQuery<DashboardNews>({
    queryKey: ['news', 'all', page],
    queryFn: () =>
      api.dashboardNews({
        limit: PAGE_SIZE,
        hours: 0,
        offset: page * PAGE_SIZE,
      }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    // keep the previous page's data visible while the next page is
    // loading — avoids the "flash to empty" when clicking 다음.
    placeholderData: (prev) => prev,
  })

  const [filter, setFilter] = useState('')
  // 'summary' is a pseudo-service that means "only rows the LLM has
  // summarised". 'crypto' is a pseudo-service that means "only rows the
  // backend routed into the crypto scope bucket" (LLM scope=crypto OR
  // keyword-matched) — useful when the operator wants Bithumb-side
  // chatter separated from stock flow on trend sources.
  const [serviceFilter, setServiceFilter] = useState<'all' | NewsService | 'summary' | 'crypto'>('all')
  const [sentimentFilter, setSentimentFilter] = useState<'all' | NewsSentiment>('all')

  // Map news id → summary (if present) so we can filter/display the
  // sentiment tag next to the raw row. Summary covers only the news set
  // the server sent to the LLM; rows without a matching summary entry
  // pass the sentiment filter only when "전체" is selected.
  const summaryByID = useMemo(() => {
    const m = new Map<string, NewsSummaryItem>()
    const items = q.data?.summary?.items ?? []
    for (const it of items) m.set(it.id, it)
    return m
  }, [q.data])

  const rows = useMemo(() => {
    if (!q.data) return []
    const needle = filter.trim().toLowerCase()

    // 요약본 tab reads straight from the summary cache. Prior implementation
    // tried to intersect summary.items with the raw feed, but those two sets
    // drift — the cache row is whatever the LLM summarised last, while the
    // live feed is everything the upstream has right now. An operator who
    // clicks "요약본" wants to see the summaries, not "summaries that happen
    // to still be in today's feed".
    if (serviceFilter === 'summary') {
      // Bucket by TICKER match against held_tickers (not by id match against
      // the current raw holdings feed). Summary items outlive raw feed rows —
      // a held position's news can still be in the summary cache an hour
      // after it dropped off the raw feed window. Using raw holdings ids
      // for the intersection would mis-tag those as market-wide.
      const heldTickers = new Set(q.data.held_tickers ?? [])
      const summaryItems = q.data.summary?.items ?? []
      const projected = summaryItems.map<NewsRow>((s) => ({
        id: s.id,
        service: s.service as NewsService,
        origin: s.origin ?? '',
        title: s.title,
        body: '',
        url: s.url ?? '',
        ticker: s.ticker ?? '',
        category: '',
        severity: '',
        region: '',
        score: 0,
        spike: false,
        published_at: s.published_at ?? 0,
        _bucket:
          s.ticker && heldTickers.has(s.ticker)
            ? ('holdings' as const)
            : ('market' as const),
      }))
      return projected
        .filter((it) => {
          if (sentimentFilter !== 'all') {
            const sum = summaryByID.get(it.id)
            if (!sum || sum.sentiment !== sentimentFilter) return false
          }
          if (!needle) return true
          return (
            it.title.toLowerCase().includes(needle) ||
            (it.ticker ?? '').toLowerCase().includes(needle) ||
            (it.origin ?? '').toLowerCase().includes(needle)
          )
        })
        .sort((a, b) => (b.published_at ?? 0) - (a.published_at ?? 0))
    }

    // Crypto tab reads its own bucket (q.data.crypto). Holdings/market
    // buckets explicitly exclude crypto rows server-side so switching
    // between tabs gives non-overlapping views — the operator can
    // inspect trend-collector 비트코인 chatter without it leaking into
    // KIS stock-side filters, and vice versa.
    if (serviceFilter === 'crypto') {
      const cryptoItems = (q.data.crypto ?? []).map<NewsRow>((it) => ({
        ...it,
        _bucket: 'crypto' as const,
      }))
      return cryptoItems
        .filter((it) => {
          if (sentimentFilter !== 'all') {
            const sum = summaryByID.get(it.id)
            if (!sum || sum.sentiment !== sentimentFilter) return false
          }
          if (!needle) return true
          return (
            it.title.toLowerCase().includes(needle) ||
            (it.body ?? '').toLowerCase().includes(needle) ||
            (it.ticker ?? '').toLowerCase().includes(needle) ||
            (it.origin ?? '').toLowerCase().includes(needle)
          )
        })
        .sort((a, b) => (b.published_at ?? 0) - (a.published_at ?? 0))
    }

    const all = [
      ...q.data.holdings.map((it) => ({ ...it, _bucket: 'holdings' as const })),
      ...q.data.market_wide.map((it) => ({ ...it, _bucket: 'market' as const })),
    ]
    return all.filter((it) => {
      if (serviceFilter !== 'all' && it.service !== serviceFilter) {
        return false
      }
      if (sentimentFilter !== 'all') {
        const sum = summaryByID.get(it.id)
        if (!sum || sum.sentiment !== sentimentFilter) return false
      }
      if (!needle) return true
      return (
        it.title.toLowerCase().includes(needle) ||
        (it.body ?? '').toLowerCase().includes(needle) ||
        (it.ticker ?? '').toLowerCase().includes(needle) ||
        (it.origin ?? '').toLowerCase().includes(needle)
      )
    })
  }, [q.data, filter, serviceFilter, sentimentFilter, summaryByID])

  const sentimentCounts = useMemo(() => {
    const out: Record<NewsSentiment | 'all', number> = {
      all: 0,
      bullish: 0,
      bearish: 0,
      neutral: 0,
      mixed: 0,
    }
    const items = q.data?.summary?.items ?? []
    for (const it of items) {
      out.all++
      out[it.sentiment]++
    }
    return out
  }, [q.data])

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">News</h1>
          <p className="text-sm text-muted-foreground">
            Trend-collector + KIS (공시 · 종합시황 · 해외속보 · 해외뉴스)
            원본 피드 — 매 60초 자동 갱신.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            placeholder="제목 · 본문 · 티커 · origin 검색"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-64"
          />
        </div>
      </header>

      <ServiceFilterBar
        current={serviceFilter}
        onChange={(v) => {
          setServiceFilter(v)
          // Sentiment filter only makes sense inside the summary tab —
          // switching back to a service tab clears it so the raw feed
          // isn't accidentally suppressed by a stale sentiment choice.
          if (v !== 'summary') setSentimentFilter('all')
        }}
        counts={serviceCounts(q.data)}
      />

      {serviceFilter === 'summary' && (
        <SentimentFilterBar
          current={sentimentFilter}
          onChange={setSentimentFilter}
          counts={sentimentCounts}
        />
      )}

      {serviceFilter === 'crypto' && (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">암호화폐 피드</span>
            {' · '}
            LLM이 scope=crypto로 분류했거나 비트코인/이더리움/블록체인 등
            키워드가 포함된 trend 행만 표시합니다. KIS 주식 피드는 암호화폐
            분류에서 제외됩니다.
          </p>
        </div>
      )}

      <NewsQueueCard />

      {q.isPending && (
        <Card>
          <Card.Body>
            <p className="text-sm text-muted-foreground">Loading news…</p>
          </Card.Body>
        </Card>
      )}

      {q.error && (
        <Card className="border-destructive-soft bg-destructive-soft">
          <Card.Body className="text-sm text-destructive-soft-foreground">
            Failed to load: {String(q.error)}
          </Card.Body>
        </Card>
      )}

      {q.data && q.data.source_errors && Object.keys(q.data.source_errors).length > 0 && (
        <Card className="border-warning-soft bg-warning-soft">
          <Card.Body>
            <h3 className="text-sm font-semibold text-warning-soft-foreground mb-1">
              업스트림 오류
            </h3>
            <ul className="space-y-1 text-xs text-warning-soft-foreground">
              {Object.entries(q.data.source_errors).map(([src, err]) => (
                <li key={src}>
                  <span className="font-mono mr-2">{src}</span>
                  <span className="font-mono break-all">{err}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-warning-soft-foreground">
              수집 건수 — {Object.entries(q.data.source_counts ?? {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '(none)'}
            </p>
          </Card.Body>
        </Card>
      )}

      {q.data && (
        <Card>
          <Card.Body>
            {(() => {
              const pg = q.data.pagination
              const pageStart = pg ? pg.offset + 1 : 0
              const pageReturned = q.data.holdings.length + q.data.market_wide.length
              const pageEnd = pg ? pg.offset + pageReturned : pageReturned
              const total = pg?.total ?? pageReturned
              const canPrev = page > 0
              const canNext = pg ? pg.has_more : false
              return (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>
                    {pageReturned > 0
                      ? `${pageStart}–${pageEnd} / ${total}건`
                      : `0 / ${total}건`}
                    {q.data.crypto && q.data.crypto.length > 0 && (
                      <span className="ml-2">· 암호화폐 {q.data.crypto.length}</span>
                    )}
                    {rows.length !== pageReturned && serviceFilter !== 'summary' && serviceFilter !== 'crypto' && (
                      <span className="ml-2">· 필터 {rows.length}건</span>
                    )}
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!canPrev}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      이전
                    </Button>
                    <span className="font-mono whitespace-nowrap">페이지 {page + 1}</span>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!canNext}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      다음
                    </Button>
                    <span className="whitespace-nowrap">fetched {formatTime(q.data.fetched_at)}</span>
                  </div>
                </div>
              )
            })()}

            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">표시할 뉴스가 없습니다.</p>
            ) : (
              <ul className="space-y-2">
                {rows.map((it) => (
                  <li key={`${it._bucket}-${it.id}`}>
                    <NewsFullRow
                      item={it}
                      bucket={it._bucket}
                      sum={summaryByID.get(it.id)}
                      summaryPending={q.data?.summary_pending ?? false}
                      onSummarized={(fresh) => {
                        // Patch the cached query data so the UI updates
                        // immediately without waiting for the next poll.
                        // Mutate the current page's cache entry — other
                        // pages' caches will resync on their own refetch.
                        qc.setQueryData<DashboardNews>(['news', 'all', page], (old) => {
                          if (!old) return old
                          const base = old.summary ?? {
                            id: 0,
                            items: [],
                            sentiment: 'neutral' as const,
                            sentiment_score: 0,
                            input_count: 0,
                            created_at: new Date().toISOString(),
                          }
                          const without = base.items.filter((i) => i.id !== fresh.id)
                          return {
                            ...old,
                            summary: {
                              ...base,
                              items: [fresh, ...without],
                            },
                          }
                        })
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

function serviceCounts(
  data?: DashboardNews,
): Record<NewsService | 'all' | 'summary' | 'crypto', number> {
  const out: Record<string, number> = { all: 0, summary: 0, crypto: 0 }
  if (!data) return out as Record<NewsService | 'all' | 'summary' | 'crypto', number>
  const all = [...data.holdings, ...data.market_wide]
  out.all = all.length
  for (const it of all) {
    out[it.service] = (out[it.service] ?? 0) + 1
  }
  // The 요약본 badge uses the SERVER-AUTHORITATIVE summary_total so it
  // matches the queue panel's "요약완료 N건" stat. summary.items.length
  // would only reflect whatever display-slice the server chose to return.
  out.summary = data.summary_total ?? data.summary?.items.length ?? 0
  // The crypto tab reads its own server-side bucket — count that, not
  // the stock-side holdings+market count.
  out.crypto = data.crypto?.length ?? 0
  return out as Record<NewsService | 'all' | 'summary' | 'crypto', number>
}

function SentimentFilterBar({
  current,
  onChange,
  counts,
}: {
  current: 'all' | NewsSentiment
  onChange: (v: 'all' | NewsSentiment) => void
  counts: Record<NewsSentiment | 'all', number>
}) {
  const chips: Array<{ key: 'all' | NewsSentiment; label: string; tone: string }> = [
    { key: 'all',     label: '요약 전체', tone: 'border-border' },
    { key: 'bullish', label: '강세',      tone: 'border-emerald-400' },
    { key: 'bearish', label: '약세',      tone: 'border-red-400' },
    { key: 'neutral', label: '중립',      tone: 'border-border' },
    { key: 'mixed',   label: '혼조',      tone: 'border-amber-400' },
  ]
  if (counts.all === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c) => {
        const n = counts[c.key] ?? 0
        const active = current === c.key
        const disabled = n === 0 && c.key !== 'all'
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onChange(c.key)}
            disabled={disabled}
            className={
              active
                ? `rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-sm border-2 ${c.tone}`
                : `rounded-full border-2 ${c.tone} bg-card px-3 py-1 text-xs font-medium text-foreground hover:bg-muted/50 disabled:opacity-40`
            }
          >
            {c.label} <span className="ml-1 opacity-70">{n}</span>
          </button>
        )
      })}
    </div>
  )
}

function ServiceFilterBar({
  current,
  onChange,
  counts,
}: {
  current: 'all' | NewsService | 'summary' | 'crypto'
  onChange: (v: 'all' | NewsService | 'summary' | 'crypto') => void
  counts: Record<NewsService | 'all' | 'summary' | 'crypto', number>
}) {
  const chips: Array<{ key: 'all' | NewsService | 'summary' | 'crypto'; label: string }> = [
    { key: 'all', label: '전체' },
    // "요약본" is the LLM-summarised subset, rendered as a sibling tab
    // so the operator can jump from raw-feed inspection to the
    // curated view with one click. Counts reflect summary.items length.
    { key: 'summary', label: '요약본' },
    // "암호화폐" is the scope=crypto bucket (trend rows classified as
    // crypto, never KIS stock feeds). Gives the operator a direct view
    // of Bithumb-relevant chatter without KIS 공시/시황 noise.
    { key: 'crypto', label: '암호화폐' },
    { key: 'trend', label: 'Trend' },
    { key: 'kis_disclosure', label: 'KIS 공시' },
    { key: 'kis_market', label: 'KIS 시황' },
    { key: 'kis_overseas_breaking', label: '해외속보' },
    { key: 'kis_overseas_news', label: '해외뉴스' },
  ]
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c) => {
        const n = counts[c.key] ?? 0
        const active = current === c.key
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onChange(c.key)}
            disabled={
              n === 0 && c.key !== 'all' && c.key !== 'summary' && c.key !== 'crypto'
            }
            className={
              active
                ? 'rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-sm'
                : 'rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-foreground hover:bg-muted/50 disabled:opacity-40'
            }
          >
            {c.label} <span className="ml-1 opacity-70">{n}</span>
          </button>
        )
      })}
    </div>
  )
}

type NewsRow = NewsItem & { _bucket: 'holdings' | 'market' | 'crypto' }

// SummarizeButton shows "재요약" when a summary already exists, "바로
// 요약" when it doesn't. Pending state swaps to a disabled "요약중…"
// so successive clicks don't fan out duplicate LLM calls.
function SummarizeButton({
  hasSummary,
  pending,
  onClick,
}: {
  hasSummary: boolean
  pending: boolean
  onClick: () => void
}) {
  const label = pending ? '요약중…' : hasSummary ? '재요약' : '바로 요약'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className="rounded-md border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted/50 disabled:opacity-50"
      title={hasSummary ? '이 뉴스를 다시 요약' : '이 뉴스만 먼저 요약'}
    >
      {label}
    </button>
  )
}

// SummaryStatusBadge renders one of three states:
//   요약완료 — this row has a matching summary in the current response
//   요약중  — no summary yet AND a background job is running. Uses a
//             pulsing dot so the operator sees activity rather than
//             just a static badge.
//   대기    — no summary and no job running (summariser disabled or
//             the feed hasn't been dispatched yet). Kept neutral-tone.
function SummaryStatusBadge({
  hasSummary,
  pending,
}: {
  hasSummary: boolean
  pending: boolean
}) {
  if (hasSummary) {
    return (
      <Badge variant="success" shape="pill">
        요약완료
      </Badge>
    )
  }
  if (pending) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-info-soft px-2 py-0.5 text-[11px] font-medium text-info-soft-foreground">
        <span
          aria-hidden="true"
          className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-info-soft-foreground"
        />
        요약중
      </span>
    )
  }
  return (
    <Badge variant="muted" shape="pill">
      요약 대기
    </Badge>
  )
}

function NewsFullRow({
  item,
  bucket,
  sum,
  summaryPending,
  onSummarized,
}: {
  item: NewsRow
  bucket: 'holdings' | 'market' | 'crypto'
  sum?: NewsSummaryItem
  summaryPending: boolean
  onSummarized: (fresh: NewsSummaryItem) => void
}) {
  const summarize = useMutation({
    mutationFn: () =>
      api.summarizeNewsItem({
        id: item.id,
        service: item.service,
        title: item.title,
        body: item.body,
        url: item.url,
        ticker: item.ticker,
        origin: item.origin,
        published_at: item.published_at,
      }),
    onSuccess: onSummarized,
  })
  const serviceLabel: Record<NewsService, string> = {
    kis_disclosure: '공시',
    kis_market: '시황',
    kis_overseas_breaking: '해외속보',
    kis_overseas_news: '해외뉴스',
    trend: 'Trend',
  }
  const serviceVariant: Record<NewsService, 'muted' | 'info' | 'warning' | 'success'> = {
    kis_disclosure: 'warning',
    kis_market: 'info',
    kis_overseas_breaking: 'warning',
    kis_overseas_news: 'info',
    trend: 'success',
  }
  const sentimentBadge: Record<
    NewsSentiment,
    { label: string; variant: 'success' | 'destructive' | 'muted' | 'warning' }
  > = {
    bullish: { label: '강세', variant: 'success' },
    bearish: { label: '약세', variant: 'destructive' },
    neutral: { label: '중립', variant: 'muted' },
    mixed:   { label: '혼조', variant: 'warning' },
  }
  const sentimentBorder: Record<NewsSentiment, string> = {
    bullish: 'border-l-emerald-400',
    bearish: 'border-l-red-400',
    neutral: 'border-l-muted',
    mixed:   'border-l-amber-400',
  }
  return (
    <div
      className={`rounded-md border border-border bg-card p-3 ${
        sum ? `border-l-4 ${sentimentBorder[sum.sentiment]}` : ''
      } ${bucket === 'holdings' ? 'ring-1 ring-primary/20' : ''}`}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={serviceVariant[item.service]} shape="square">
            {serviceLabel[item.service]}
          </Badge>
          <SummaryStatusBadge hasSummary={!!sum} pending={summaryPending} />
          {sum && (
            <Badge variant={sentimentBadge[sum.sentiment].variant} shape="pill">
              {sentimentBadge[sum.sentiment].label}
            </Badge>
          )}
          {bucket === 'crypto' && (
            <Badge variant="warning" shape="pill">
              암호화폐
            </Badge>
          )}
          {bucket === 'holdings' && (
            <Badge variant="info" shape="pill">
              보유
            </Badge>
          )}
          {item.ticker && (
            <span className="text-xs text-muted-foreground">
              <span className="font-mono">{item.ticker}</span>
              {krxName(item.ticker) && (
                <span className="ml-1">{krxName(item.ticker)}</span>
              )}
            </span>
          )}
          {item.severity === 'material' && (
            <Badge variant="destructive" shape="pill">
              material
            </Badge>
          )}
          {item.severity && item.severity !== 'material' && (
            <Badge variant="muted" shape="pill">
              {item.severity}
            </Badge>
          )}
          {item.spike && (
            <Badge variant="warning" shape="pill">
              spike
            </Badge>
          )}
          {item.category && (
            <span className="text-xs text-muted-foreground">{item.category}</span>
          )}
          {item.region && (
            <span className="text-xs text-muted-foreground">{item.region}</span>
          )}
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <SummarizeButton
            hasSummary={!!sum}
            pending={summarize.isPending}
            onClick={() => summarize.mutate()}
          />
          <span
            className="text-xs text-muted-foreground"
            title={item.published_at ? formatTime(item.published_at) : ''}
          >
            {item.published_at ? formatRelativeKST(item.published_at) : ''}
          </span>
        </div>
      </div>
      {summarize.isError && (
        <p className="mt-1 text-[11px] text-red-600">
          요약 실패: {String(summarize.error)}
        </p>
      )}

      <h3 className="text-sm font-medium leading-snug">
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {item.title}
            <span aria-hidden="true" className="ml-1 text-xs text-muted-foreground">↗</span>
          </a>
        ) : (
          item.title
        )}
      </h3>

      {sum && (
        <p className="mt-1 text-sm leading-relaxed">{sum.summary_ko}</p>
      )}

      {item.body && (
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap">
          {item.body}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground font-mono">
        <span>id={item.id}</span>
        {item.origin && <span>origin={item.origin}</span>}
        {typeof item.score === 'number' && <span>score={item.score.toFixed(2)}</span>}
        {sum && <span>sent_score={sum.sentiment_score.toFixed(2)}</span>}
      </div>
    </div>
  )
}

// NewsQueueCard surfaces the worker queue: how many rows are pending
// LLM summary, how many are done, when the last summary landed, plus a
// "지금 실행" button that fires one immediate worker tick. Suspected
// duplicates render as a sub-list so the operator can confirm whether
// 2 rows with the same title are real reposts (different ids = real)
// vs an internal storage bug (same id would be impossible — INSERT OR
// IGNORE on PK).
function NewsQueueCard() {
  const qc = useQueryClient()
  const q = useQuery<NewsQueueResponse>({
    queryKey: ['news', 'queue'],
    queryFn: () => api.newsQueue(),
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
  })
  const trigger = useMutation({
    mutationFn: () => api.newsRunNow(),
    onSuccess: () => {
      // Re-poll the queue immediately so the operator sees worker_inflight
      // flip to true within a tick.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['news', 'queue'] }), 500)
      setTimeout(() => qc.invalidateQueries({ queryKey: ['news', 'all'] }), 5_000)
    },
  })
  const dedup = useMutation({
    mutationFn: () => api.newsDedup(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news', 'queue'] })
      qc.invalidateQueries({ queryKey: ['news', 'all'] })
    },
  })
  const resetPending = useMutation({
    mutationFn: () => api.newsResetPending(),
    onSuccess: (res) => {
      // Surface how many rows returned to the queue; poll /queue right
      // away so the pending count reflects the reset.
      if (res?.rows_reset !== undefined) {
        // eslint-disable-next-line no-alert -- quick operator feedback
        alert(`${res.rows_reset}건 대기열로 복귀했습니다`)
      }
      qc.invalidateQueries({ queryKey: ['news', 'queue'] })
    },
  })

  if (!q.data) return null
  const d = q.data
  const elapsedSinceLastSummary = d.last_summary_at
    ? Math.max(0, Math.floor(Date.now() / 1000) - d.last_summary_at)
    : null
  const oldestPendingAge = d.oldest_pending_at
    ? Math.max(0, Math.floor(Date.now() / 1000) - d.oldest_pending_at)
    : null

  return (
    <Card>
      <Card.Header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Card.Title className="flex items-center gap-2">
            요약 큐
            {d.worker_inflight && (
              <Badge variant="info" shape="pill">
                <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-info-soft-foreground" />
                실행 중
              </Badge>
            )}
            {d.last_failure && (
              <Badge variant="destructive" shape="pill">
                최근 실패
              </Badge>
            )}
          </Card.Title>
          <Card.Description>
            24시간 이내 수집된 뉴스 기준. 워커는 5분마다 chunk_size 만큼만 처리.
          </Card.Description>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (
                // eslint-disable-next-line no-alert -- confirm one-click bulk action
                window.confirm(
                  '대기 중(요약 미완료) 뉴스의 재시도 카운터를 전부 0으로 되돌립니다. 진행할까요?',
                )
              ) {
                resetPending.mutate()
              }
            }}
            disabled={resetPending.isPending}
            loading={resetPending.isPending}
            title="재시도 횟수를 초과해 멈춘 대기 뉴스를 워커 큐로 복귀"
          >
            대기열 초기화
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => trigger.mutate()}
            disabled={trigger.isPending || d.worker_inflight}
            loading={trigger.isPending}
            title={
              d.worker_inflight
                ? '워커가 이미 실행 중입니다'
                : '대기 중인 뉴스 chunk_size 만큼 즉시 요약'
            }
          >
            지금 실행
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5 mb-3">
          <Stat
            label="재시도 대기"
            value={d.pending_retryable}
            tone={d.pending_retryable > 0 ? 'warning' : 'muted'}
          />
          <Stat
            label="재시도 소진"
            value={d.pending_exhausted}
            tone={d.pending_exhausted > 0 ? 'warning' : 'muted'}
          />
          <Stat label="요약 완료" value={d.summarised} tone="success" />
          <Stat
            label="마지막 요약"
            value={
              elapsedSinceLastSummary === null
                ? '없음'
                : formatElapsed(elapsedSinceLastSummary) + ' 전'
            }
          />
          <Stat
            label="가장 오래된 대기"
            value={
              oldestPendingAge === null
                ? '-'
                : formatElapsed(oldestPendingAge) + ' 전'
            }
            tone={oldestPendingAge !== null && oldestPendingAge > 600 ? 'warning' : 'muted'}
          />
        </div>

        {Object.keys(d.by_source).length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            <span className="text-muted-foreground">대기 중 소스별:</span>
            {Object.entries(d.by_source)
              .sort((a, b) => b[1] - a[1])
              .map(([svc, n]) => (
                <span
                  key={svc}
                  className="rounded-full border border-border bg-muted/40 px-2 py-0.5 font-mono"
                >
                  {svc}: {n}
                </span>
              ))}
          </div>
        )}

        {d.last_failure && (
          <div className="mb-3 rounded-md border border-destructive-soft bg-destructive-soft p-2 text-xs text-destructive-soft-foreground">
            <p className="font-medium">최근 워커 실패</p>
            <p className="font-mono break-all">{d.last_failure}</p>
            {d.last_failed_at && (
              <p className="mt-0.5 text-[11px] opacity-80">
                {formatTime(d.last_failed_at)}
              </p>
            )}
          </div>
        )}

        {d.duplicates && d.duplicates.length > 0 && (
          <div className="rounded-md border border-warning-soft bg-warning-soft p-2 text-xs text-warning-soft-foreground">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div>
                <p className="font-medium">
                  중복 의심 그룹 {d.duplicates.length}건 — 같은 제목이 2회
                  이상 저장됨
                </p>
                <p className="mt-1 text-[11px] opacity-80">
                  ID가 모두 다르면 업스트림(증권사 측) 실제 재게시입니다.
                  같은 제목으로 여러 row가 쌓이면 LLM 요약 예산이 낭비되고
                  대시보드 시그널이 한쪽으로 기울 수 있습니다. "정리" 버튼은
                  그룹마다 1건만 남깁니다 (요약된 row 우선, 없으면 가장 최근
                  수집된 row).
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                disabled={dedup.isPending}
                loading={dedup.isPending}
                onClick={() => dedup.mutate()}
                title="감지된 중복 그룹을 한 row만 남기고 정리"
              >
                중복 정리
              </Button>
            </div>
            {dedup.isError && (
              <p className="mb-2 text-[11px] text-destructive">
                정리 실패: {String(dedup.error)}
              </p>
            )}
            {dedup.data && (
              <p className="mb-2 rounded border border-success-soft bg-success-soft px-2 py-1 text-[11px] text-success-soft-foreground">
                ✓ {dedup.data.groups} 그룹에서 {dedup.data.rows_deleted}건 삭제
                ({dedup.data.rows_kept}건 유지)
              </p>
            )}
            <ul className="space-y-1">
              {d.duplicates.slice(0, 5).map((g, i) => (
                <li key={i} className="rounded border border-border/50 bg-card/40 p-1.5">
                  <div className="font-medium">
                    {g.normalised_title.slice(0, 80)}
                    {g.normalised_title.length > 80 && '…'}{' '}
                    <span className="opacity-70">×{g.count}</span>
                  </div>
                  <div className="font-mono text-[11px] opacity-70">
                    {g.services.join(' · ')} · ids: {g.ids.join(', ')}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function Stat({
  label,
  value,
  tone = 'muted',
}: {
  label: string
  value: string | number
  tone?: 'muted' | 'success' | 'warning'
}) {
  const valueClass =
    tone === 'success'
      ? 'text-emerald-500'
      : tone === 'warning'
      ? 'text-amber-500'
      : 'text-foreground'
  return (
    <div className="rounded-md border border-border bg-card p-2">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${valueClass}`}>{value}</div>
    </div>
  )
}

// formatElapsed prints a coarse "Xs / Xm / Xh / Xd" depending on
// magnitude. Tightly-scoped to the queue card; not worth pulling into
// a shared util.
function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}초`
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}분`
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}시간`
  return `${Math.floor(seconds / 86_400)}일`
}
